import React, { useState } from 'react';
import styled from '@emotion/styled';

const FAQContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(9, 30, 66, 0.08), 0 1px 2px rgba(9, 30, 66, 0.08);
  padding: 40px;
  margin-top: 32px;
  width: 100%;
  max-width: 100%;
  border: 1px solid rgba(9, 30, 66, 0.08);
  backdrop-filter: blur(12px);
`;

const FAQHeader = styled.div`
  margin-bottom: 40px;
`;

const FAQTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: 28px;
  color: #172B4D;
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const FAQTitleIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #EAE6FF;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5243AA;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background-color: #DFD8FD;
  }
`;

const FAQDescription = styled.p`
  color: #5E6C84;
  font-size: 18px;
  margin: 12px 0 0 0;
  line-height: 1.6;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div`
  border: 1px solid #EBECF0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.15s ease;
  background: white;
  position: relative;

  &:hover {
    border-color: #EBECF0;
    box-shadow: 0 2px 8px rgba(9, 30, 66, 0.05);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
    opacity: 0;
    background: rgba(9, 30, 66, 0.02);
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  text-align: left;
  padding: 24px 32px;
  background: transparent;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 17px;
  color: #172B4D;
  font-weight: 600;
  line-height: 1.4;
  position: relative;
  z-index: 1;

  svg {
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 20px;
    margin-left: 16px;
    opacity: 0.7;
  }
`;

const FAQAnswer = styled.div`
  padding: ${props => props.isOpen ? '0 32px 24px' : '0 32px'};
  color: #42526E;
  font-size: 16px;
  line-height: 1.6;
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  z-index: 1;
  
  p {
    margin: 0;
  }
`;

const FAQ = ({ faqs = [] }) => {
  const [openIndices, setOpenIndices] = useState([]);

  const toggleFAQ = (index) => {
    setOpenIndices(prevIndices => {
      if (prevIndices.includes(index)) {
        return prevIndices.filter(i => i !== index);
      } else {
        return [...prevIndices, index];
      }
    });
  };

  const defaultFAQs = [
    {
      question: "What are the image size requirements for the Marketplace listing?",
      answer: "Your App Logo should be 144x144px (PNG/JPG), the App Banner should be 1120x548px (high-res) or 560x274px (standard), and Highlight Screenshots or images should be 1840x900px (high-res) or 920x450px (standard)."
    },
    {
      question: "What are the character limits for different sections of the Atlassian Marketplace app listing?",
      answer: "App name: 60 characters, App tagline: 130 characters, Highlight titles: 50 characters each, Highlight summaries: 220 characters each, More details: 1000 characters."
    },
    {
      question: "Can I save or share my previews?",
      answer: "Yes, you can download your preview in multiple formats including PNG, PDF, text, and spreadsheet. These can be easily shared with your team for review and feedback. Please bear in mind that the PDF preview may not match the final Marketplace listing exactly, but it will give you a good idea of what your listing will look like."
    },
    {
      question: "What types of media can I include in my listing?",
      answer: "You can include images (logo, banner, and highlight screenshots) and a YouTube video URL. The video will be displayed prominently in your listing and can help showcase your app's features."
    },
    {
      question: "How is my data handled when using this tool?",
      answer: "This tool operates entirely in your browser - no data is stored on our servers. When you create and download previews, all processing happens locally on your device. Your content remains private and is automatically cleared when you close the browser tab. Downloaded preview files are saved only to your local device."
    },
    {
      question: "Is this affiliated with Atlassian?",
      answer: "No, this tool is not affiliated with Atlassian in any way. It was created as a personal tool and published for public use."
    },
    {
      question: "What are best practices for creating an effective app listing?",
      answer: "Focus on clear, benefit-driven content that explains what your app does and why it matters. Use high-quality images that showcase your app's interface and features. Include a concise, compelling tagline. Make sure your highlights address key user pain points. A demo video can significantly increase conversion rates. Finally, ensure all content is error-free and professionally presented."
    },
    {
      question: "How can I optimize my app listing for better visibility?",
      answer: "Include relevant keywords in your app name, tagline, and descriptions without keyword stuffing. Focus on solving specific problems in your highlight sections. Use clear, benefit-driven language. Showcase real use cases in your screenshots. Regularly update your listing with new features and improvements. Respond promptly to user reviews and questions to build trust and engagement."
    },
    {
      question: "What file formats are supported for images?",
      answer: "This tool supports JPG/JPEG and PNG image formats for all image uploads (logo, banner, and highlight screenshots). For best results, use PNG format for logos, especially if they contain transparency, and high-quality JPG/PNG for banners and screenshots."
    },
    {
      question: "Can I edit my preview after creating it?",
      answer: "Yes, you can edit any part of your preview at any time by simply updating the form fields. The preview will update in real-time as you make changes. You can then download the updated version in your preferred format. However, remember that your data is not saved between sessions, so you'll need to complete your work in a single browser session."
    },
    {
      question: "What should I include in my YouTube video?",
      answer: "Your video should be concise (2-3 minutes is ideal) and demonstrate your app's key features and benefits. Include a brief introduction to the problem your app solves, show the app in action with real use cases, highlight unique selling points, and end with a clear call to action. Professional narration and captions can make your video more accessible and engaging."
    },
    {
      question: "How do I choose which Atlassian products to select in the 'Works with' section?",
      answer: "These are just examples. Your app may work with other Atlassian products, but we've opted to only include a few of the most popular ones as options in this preview tool. These won't impact your real app, so if you can't find the right product, just select one of the available product options."
    }
  ];

  const faqsToDisplay = faqs.length > 0 ? faqs : defaultFAQs;

  return (
    <FAQContainer>
      <FAQHeader>
        <FAQTitle>
          <FAQTitleIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
            </svg>
          </FAQTitleIcon>
          Frequently Asked Questions
        </FAQTitle>
        <FAQDescription>Find answers to common questions about creating your Marketplace listing</FAQDescription>
      </FAQHeader>
      <FAQList>
        {faqsToDisplay.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion
              onClick={() => toggleFAQ(index)}
              isOpen={openIndices.includes(index)}
            >
              {faq.question}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.292 10.293a1.009 1.009 0 0 0 0 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 0 0 0-1.419.987.987 0 0 0-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 0 0-1.406 0z"/>
              </svg>
            </FAQQuestion>
            <FAQAnswer isOpen={openIndices.includes(index)}>
              {faq.answer}
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQList>
    </FAQContainer>
  );
};

export default FAQ; 
